import React, { useCallback } from "react";
import { FormGroup, Checkbox, Button, InputGroup } from "@blueprintjs/core";
import { DataManager, Predicate } from "@syncfusion/ej2-data";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";
import {
  FieldLabeledProps,
  ODataCheckboxFieldValue,
} from "../../types/fieldTypes";
import { useODataCheckboxField } from "./useODataCheckboxField";
import { UseBaseODataManagerHookProps } from "../../../../stores/OData/useBaseODataManager";

export interface ODataCheckboxFieldProps {
  // Data source configuration
  dataManager?: DataManager;
  odataProps?: UseBaseODataManagerHookProps;

  // Field configuration
  fieldName: string; // Field name for Query.distinct
  value?: ODataCheckboxFieldValue;
  onSubmit?: (value: ODataCheckboxFieldValue) => void;

  // UI configuration
  label?: string;
  description?: string;
  isRequired?: boolean;
  disabled?: boolean;
  errorMessages?: string[];
  labelProps?: FieldLabeledProps;

  // Search and pagination
  enableSearch?: boolean;
  searchPlaceholder?: string;
  pageSize?: number;
  showMoreButtonText?: string;
  onlyShowSearchWhenMoreAvailable?: boolean;

  // Additional query options - probably will deprecate
  additionalConditions?: Predicate[];
  sortBy?: string;
  sortOrder?: "ascending" | "descending";

  // Select All functionality
  showSelectAll?: boolean;
}

const ODataCheckboxField: React.FC<ODataCheckboxFieldProps> = (props) => {
  const {
    localValues,
    setLocalValues,
    filteredOptions,
    searchText,
    setSearchText,
    hasMore,
    loadMore,
    isLoading,
    error,
    hasSearched,
    allRecordsLoaded,
  } = useODataCheckboxField(props);

  const handleCheckboxChange = useCallback(
    (optionValue: string | number, checked: boolean) => {
      let newValues: ODataCheckboxFieldValue;

      if (checked) {
        newValues = [...(localValues || []), optionValue];
      } else {
        newValues = (localValues || []).filter(
          (val: string | number) => val !== optionValue
        );
      }

      setLocalValues(newValues);
      props.onSubmit?.(newValues);
    },
    [localValues, props.onSubmit]
  );

  const renderCheckboxes = useCallback(
    (
      options: Array<{
        label: string;
        value: string | number;
        isSelectedButNotMatching?: boolean;
      }>
    ) => {
      return options.map((option, idx) => {
        const checked = localValues?.includes(option.value) || false;
        const isSelectedButNotMatching =
          option.isSelectedButNotMatching || false;

        return (
          <Checkbox
            key={`${option.value}-${idx}`}
            label={option.label}
            checked={checked}
            disabled={props.disabled}
            onChange={(e) =>
              handleCheckboxChange(option.value, e.currentTarget.checked)
            }
            style={{
              opacity: isSelectedButNotMatching ? 0.6 : 1,
              color: isSelectedButNotMatching ? "#666" : "inherit",
            }}
          />
        );
      });
    },
    [localValues, props.disabled, handleCheckboxChange]
  );

  if (error) {
    return (
      <FormGroup>
        {props.label && (
          <FieldLabel
            labelName={props.label}
            tooltipDescription={props.description}
            fieldProps={{ ...props, value: localValues }}
            isRequired={props.isRequired}
            {...props.labelProps}
          />
        )}
        <div className="error-container">Error loading data: {error}</div>
      </FormGroup>
    );
  }

  return (
    <div className="ucl-odata-checkbox-field-container">
      <FormGroup
        className={`${
          props.isRequired && (!localValues || localValues.length === 0)
            ? "has-required-background"
            : "base-field"
        }`}
      >
        {props.label && (
          <FieldLabel
            labelName={props.label}
            tooltipDescription={props.description}
            fieldProps={{ ...props, value: localValues }}
            isRequired={props.isRequired}
            {...props.labelProps}
          />
        )}

        {/* Search Bar */}
        {props.enableSearch &&
          (props.onlyShowSearchWhenMoreAvailable === true ||
            hasMore ||
            hasSearched) && (
            <div className="search-container">
              <InputGroup
                placeholder={props.searchPlaceholder || "Search options..."}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                disabled={props.disabled}
                leftIcon="search"
              />
            </div>
          )}

        {/* Loading State */}
        {isLoading && (
          <div className="loading-container">Loading options...</div>
        )}

        {/* Checkboxes */}
        {!isLoading && (
          <div className="checkboxes-container">
            {/* Select All checkbox - only show when showSelectAll is true, all records are loaded, and not searching */}
            {props.showSelectAll &&
              allRecordsLoaded &&
              !hasSearched &&
              filteredOptions.length > 0 && (
                <Checkbox
                  name="Select All"
                  label="Select All"
                  disabled={props.disabled}
                  checked={localValues?.length === filteredOptions.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const allValues = filteredOptions.map(
                        (option) => option.value
                      );
                      setLocalValues(allValues);
                      props.onSubmit?.(allValues);
                    } else {
                      setLocalValues([]);
                      props.onSubmit?.([]);
                    }
                  }}
                />
              )}

            {renderCheckboxes(filteredOptions)}

            {/* More Button */}
            {hasMore && (
              <div className="more-button-container">
                <Button
                  text={props.showMoreButtonText || "Load More"}
                  onClick={loadMore}
                  disabled={props.disabled}
                  minimal
                  small
                />
              </div>
            )}
          </div>
        )}

        {props.errorMessages && (
          <FormFieldErrorContainer errorMessages={props.errorMessages} />
        )}
      </FormGroup>
    </div>
  );
};

export default ODataCheckboxField;
